import MuiAlert, { AlertProps } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Snackbar from '@mui/material/Snackbar';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';

import { Notification } from '~/tools/js/Shared/stores/typings';

export interface NotificationSystemHandlers {
    addNotification: (notification: Notification) => void;
    removeNotification: (notification: Notification) => void;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function NotificationSystemComp(props, ref) {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Array<Notification>>([]);
    const [notificationTimeoutDuration, setNotificationTimeoutDuration] = useState(5000);
    const [notificationTimeoutHandle, setNotificationTimeoutHandle] = useState<number | undefined>();
    const [portal, setPortal] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        const portalEl = document.createElement('div');
        portalEl.classList.add('ct-notification__portal');
        portalEl.style.zIndex = '100000';
        portalEl.style.position = 'fixed';
        document.body.appendChild(portalEl);
        setPortal(portalEl);
        return () => {
            document.body.removeChild(portalEl);
        };
    }, []);

    const addNotification = (notification) => {
        if (notifications.filter((n) => n.uid === notification.uid).length === 0) {
            setNotifications([...notifications, notification]);
        }
        clearTimeout(notificationTimeoutHandle);
        (function openNotification() {
            if (!open) {
                setOpen(true);
            } else {
                setNotificationTimeoutHandle(() => {
                    const handle = window.setTimeout(() => {
                        openNotification();
                    }, notificationTimeoutDuration + 100);
                    return handle;
                });
            }
        })();
    };

    const removeNotification = (notification) => {
        notifications[0].onRemove?.();
        setNotifications(notifications.filter((n) => n.uid !== notification.uid));
    };

    const handleClose = () => {
        setOpen(false);
        removeNotification(notifications[0]);
    };

    useImperativeHandle<any, NotificationSystemHandlers>(
        ref,
        (): NotificationSystemHandlers => ({
            addNotification,
            removeNotification,
        }),
    );

    // TODO: put this in its own context remove dep on redux for this
    return notifications[0] && portal
        ? createPortal(
              <>
                  <Snackbar
                      // TODO: should we disable the clickaway?
                      ClickAwayListenerProps={{
                          mouseEvent: 'onMouseDown',
                          touchEvent: 'onTouchStart',
                          // onClickAway:{handleClickAway}
                      }}
                      open={open}
                      autoHideDuration={notificationTimeoutDuration}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  >
                      <Alert onClose={handleClose} severity={notifications[0].level} sx={{ width: '100%' }}>
                          <AlertTitle>{notifications[0].title}</AlertTitle>
                          {notifications[0].message}
                      </Alert>
                  </Snackbar>
              </>,
              portal,
          )
        : null;
}

const NotificationSystem = forwardRef<any, any>(NotificationSystemComp);

export { NotificationSystem };
